import { useCallback, useEffect, useRef, useState } from 'react';
import { artifactUrl, getMe, newSession, streamChat } from '@/services/agenticApi';
import type { ChatMessage } from '@/types';

const FALLBACK_WELCOME =
  "Hello! Your domain expert agent (Load and Infrastructure Specialist) is ready, and " +
  "the **charging_sessions.csv** dataset is preloaded. Ask me anything " +
  "about charging-session statistics, plots, maps, or grid-load patterns.";

export const useChat = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sessionRef = useRef<string | null>(null);
  const welcomeRef = useRef<string>(FALLBACK_WELCOME);

  // Pull the profile-driven welcome message once.
  useEffect(() => {
    const username = localStorage.getItem('agentic.username') || 'dot';
    getMe(username)
      .then((p) => {
        if (p.welcome_message) welcomeRef.current = p.welcome_message;
      })
      .catch(() => {
        // keep fallback
      });
  }, []);

  const showWelcomeGreeting = useCallback(() => {
    setMessages((prev) => {
      if (prev.length > 0) return prev;
      return [
        {
          role: 'assistant',
          content: welcomeRef.current,
          agent: 'Orchestrator',
          timestamp: new Date(),
        },
      ];
    });
  }, []);

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim()) return;

    const username = localStorage.getItem('agentic.username') || 'dot';
    const userMessage: ChatMessage = {
      role: 'user',
      content,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);

    // Append a placeholder assistant message we mutate as events arrive.
    const placeholderTs = new Date();
    setMessages((prev) => [
      ...prev,
      { role: 'assistant', content: '', agent: 'Orchestrator', timestamp: placeholderTs },
    ]);

    const updateAssistant = (patch: (m: ChatMessage) => ChatMessage) => {
      setMessages((prev) => {
        const next = [...prev];
        for (let i = next.length - 1; i >= 0; i--) {
          if (next[i].role === 'assistant' && next[i].timestamp === placeholderTs) {
            next[i] = patch(next[i]);
            break;
          }
        }
        return next;
      });
    };

    try {
      if (!sessionRef.current) {
        try {
          sessionRef.current = await newSession(username);
        } catch (e) {
          console.warn('newSession failed; continuing without one:', e);
        }
      }

      const toolPills: string[] = [];
      const artifactBlocks: string[] = [];
      let textBuf = '';
      let chosenAgent: string | undefined;

      const render = () => {
        const pillsLine = toolPills.length ? toolPills.join(' ') + '\n\n' : '';
        const body = textBuf || (toolPills.length ? '_Working…_' : '');
        return pillsLine + body + artifactBlocks.join('');
      };

      for await (const ev of streamChat({
        username,
        message: content,
        sessionUuid: sessionRef.current ?? undefined,
      })) {
        switch (ev.type) {
          case 'session':
            sessionRef.current = ev.data.session_uuid;
            break;
          case 'agent_selected':
            chosenAgent = ev.data.agent;
            updateAssistant((m) => ({ ...m, agent: chosenAgent }));
            break;
          case 'tool_call':
            toolPills.push(`\`🔧 ${ev.data.tool}…\``);
            updateAssistant((m) => ({ ...m, content: render() }));
            break;
          case 'tool_result': {
            for (let i = toolPills.length - 1; i >= 0; i--) {
              if (toolPills[i].includes(ev.data.tool) && toolPills[i].includes('🔧')) {
                toolPills[i] = ev.data.ok
                  ? `\`✅ ${ev.data.tool}\``
                  : `\`❌ ${ev.data.tool}\``;
                break;
              }
            }
            if (ev.data.ok) {
              for (const a of ev.data.artifacts || []) {
                const url = artifactUrl(a.url);
                if (a.type === 'image') {
                  artifactBlocks.push(`\n\n![artifact](${url})`);
                } else if (a.type === 'map') {
                  artifactBlocks.push(
                    `\n\n<iframe src="${url}" width="100%" height="420" ` +
                      `style="border:none;border-radius:8px;margin-top:8px;" ` +
                      `title="map artifact"></iframe>`,
                  );
                } else {
                  artifactBlocks.push(`\n\n[artifact](${url})`);
                }
              }
            }
            updateAssistant((m) => ({ ...m, content: render() }));
            break;
          }
          case 'agent_text':
          case 'orchestrator_text':
            textBuf += ev.data.text;
            updateAssistant((m) => ({ ...m, content: render() }));
            break;
          case 'error':
            throw new Error(ev.data.message);
          default:
            break;
        }
      }

      updateAssistant((m) => ({
        ...m,
        agent: chosenAgent || m.agent || 'Orchestrator',
        content: render() || '_(no response)_',
      }));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
      setError(errorMessage);
      updateAssistant((m) => ({
        ...m,
        content: `Error: ${errorMessage}`,
      }));
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
    sessionRef.current = null;
  }, []);

  const clearMessagesAndShowWelcome = useCallback(() => {
    sessionRef.current = null;
    setMessages([
      {
        role: 'assistant',
        content: welcomeRef.current,
        agent: 'Orchestrator',
        timestamp: new Date(),
      },
    ]);
    setError(null);
  }, []);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearMessages,
    clearMessagesAndShowWelcome,
    showWelcomeGreeting,
  };
};
